import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import DataTable from "../../../components/DataTable";
import {
  getCopyTradeMasterStatus,
  getCopyTradeMasterSubscribers,
} from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { isCopyTradeDisabled } from "../../../utils/permissionHelper";
import { BecomeMasterModal } from "./CopyTrade";
import "../../css/List.css";
import "../../css/CopyTrade.css";
import "../../css/MySubscriptions.css";

const PER_PAGE = 5;
const EMPTY_COMMISSION_FLOW = Array.from({ length: 7 }, (_, index) => ({
  week: `Wk${index + 1}`,
  income: 0,
  payback: 0,
}));

const AVATAR_COLORS = [
  "#3A3A3A",
  "#2A3A4A",
  "#6B5B95",
  "#4A5568",
  "#2D4A3E",
  "#5A3A2A",
];

const formatUsd = (value) =>
  `USD ${Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const mapSubscriberFromApi = (row) => ({
  id: Number(row?.ID ?? row?.id ?? 0),
  clientAccount: String(row?.CLIENT_ACCOUNT ?? row?.clientAccount ?? "—"),
  subscriptionDate: row?.SUBSCRIPTION_DATE ?? row?.subscriptionDate ?? null,
  status: String(row?.STATUS ?? row?.status ?? "inactive").toLowerCase(),
});

const extractSubscribersPayload = (response) => {
  const data = response?.decryptedData || response?.data || {};
  const payload = data?.DATA ?? data?.data ?? [];
  if (Array.isArray(payload)) {
    return { rows: payload, total: payload.length };
  }
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  return {
    rows,
    total: Number(payload?.total ?? rows.length),
  };
};

const pickAvatarColor = (seed) => {
  const text = String(seed || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const getUserDisplayName = (user) => {
  if (!user) return "";
  const info =
    user.user_information ||
    user.userInformation ||
    user.USER_INFORMATION ||
    {};
  const fullFromInfo = String(
    info.FULL_NAME ?? info.fullName ?? info.full_name ?? ""
  ).trim();
  if (fullFromInfo) return fullFromInfo;

  const first = String(
    info.FIRST_NAME ??
      info.firstName ??
      info.first_name ??
      user.FIRST_NAME ??
      user.firstName ??
      user.first_name ??
      ""
  ).trim();
  const last = String(
    info.LAST_NAME ??
      info.lastName ??
      info.last_name ??
      user.LAST_NAME ??
      user.lastName ??
      user.last_name ??
      ""
  ).trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;

  return String(
    user.FULL_NAME ??
      user.fullName ??
      user.NAME ??
      user.name ??
      user.NICK_NAME ??
      user.nickName ??
      user.USER_NAME ??
      user.userName ??
      ""
  ).trim();
};

const mapMasterProfile = (master, fallbackUser) => {
  const name = String(
    master?.NAME ?? master?.name ?? getUserDisplayName(fallbackUser) ?? ""
  ).trim();
  const loginRaw = master?.LOGIN ?? master?.login ?? "";
  const login = loginRaw === null || loginRaw === undefined || loginRaw === ""
    ? ""
    : String(loginRaw).trim();
  const commission = Number(
    master?.COMMISSION_PERCENT ?? master?.commissionPercent ?? master?.commission ?? 0
  );
  const commissionLogin = Number(
    master?.COMMISSION_LOGIN ?? master?.commissionLogin ?? login ?? 0
  );

  return {
    id: Number(master?.ID ?? master?.id ?? 0),
    name: name || "Master Trader",
    login: login || "—",
    commission: Number.isFinite(commission) ? commission : 0,
    commissionLogin: Number.isFinite(commissionLogin) ? commissionLogin : Number(login) || 0,
    profitShare: 0,
    profitShareGrowth: null,
    netCommission: 0,
    netCommissionGrowth: null,
    commissionFlow: EMPTY_COMMISSION_FLOW,
    verified: true,
    avatarColor: pickAvatarColor(name || login),
    initials: (name.charAt(0) || "M").toUpperCase(),
  };
};

const niceCeil = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 10;
  const mag = 10 ** Math.floor(Math.log10(n));
  const norm = n / mag;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * mag;
};

const formatAxisUsd = (value) => {
  const abs = Math.abs(Number(value) || 0);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  if (abs >= 10) return `${sign}$${Math.round(abs)}`;
  return `${sign}$${abs.toFixed(abs > 0 && abs < 1 ? 2 : 0)}`;
};

const mapCommissionFlow = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return EMPTY_COMMISSION_FLOW;
  return list.map((row, index) => ({
    week: String(row?.LABEL ?? row?.label ?? row?.week ?? `Wk${index + 1}`),
    income: Math.max(0, Number(row?.INCOME ?? row?.income ?? 0) || 0),
    payback: Math.max(0, Number(row?.PAYBACK ?? row?.payback ?? 0) || 0),
  }));
};

function VolumeFlowChart({ data, t }) {
  const peakIncome = Math.max(...data.map((d) => Number(d.income) || 0), 0);
  const peakPayback = Math.max(...data.map((d) => Number(d.payback) || 0), 0);
  const peak = Math.max(peakIncome, peakPayback, 0);
  const yMax = niceCeil(peakIncome > 0 ? peakIncome : peak || 10);
  const yMin = peakPayback > 0 ? niceCeil(peakPayback) : Math.max(yMax * 0.2, 1);
  const totalRange = yMax + yMin;
  const zeroOffsetPct = (yMax / totalRange) * 100;

  const yLabels = [
    formatAxisUsd(yMax),
    formatAxisUsd(yMax / 2),
    formatAxisUsd(0),
    formatAxisUsd(-yMin / 2),
    formatAxisUsd(-yMin),
  ];

  const slotCount = Math.max(7, data.length || 1);
  const slotPct = `${100 / slotCount}%`;

  return (
    <div className="msub-volume-chart">
      <div className="msub-volume-y-axis">
        {yLabels.map((label, index) => (
          <span key={`y-${index}`}>{label}</span>
        ))}
      </div>
      <div className="msub-volume-plot">
        <div className="msub-volume-plot-inner">
          <div className="msub-volume-grid">
            {yLabels.map((label, index) => (
              <span key={`grid-${index}`} />
            ))}
          </div>
          <div className="msub-volume-zero" style={{ top: `${zeroOffsetPct}%` }} />
          <div className="msub-volume-bars">
            {data.map((item, index) => {
              const incomePct =
                item.income > 0 ? Math.max(2, (item.income / totalRange) * 100) : 0;
              const paybackPct =
                item.payback > 0 ? Math.max(2, (item.payback / totalRange) * 100) : 0;
              return (
                <div
                  key={`${item.week}-${index}`}
                  className="msub-volume-stack"
                  style={{ flex: `0 0 ${slotPct}`, width: slotPct, maxWidth: slotPct }}
                >
                  {item.income > 0 ? (
                    <div
                      className="msub-volume-bar msub-volume-bar--income"
                      style={{
                        height: `${incomePct}%`,
                        bottom: `${100 - zeroOffsetPct}%`,
                      }}
                      title={`${t("msub_legend_income", {
                        ns: "mobile",
                        defaultValue: "Income",
                      })}: ${formatUsd(item.income)}`}
                    />
                  ) : null}
                  {item.payback > 0 ? (
                    <div
                      className="msub-volume-bar msub-volume-bar--payback"
                      style={{
                        height: `${paybackPct}%`,
                        top: `${zeroOffsetPct}%`,
                      }}
                      title={`${t("msub_legend_paybacks", {
                        ns: "mobile",
                        defaultValue: "Pay-backs",
                      })}: ${formatUsd(item.payback)}`}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        <div className="msub-volume-weeks">
          {data.map((item, index) => (
            <span
              key={`week-${item.week}-${index}`}
              className="msub-volume-week"
              style={{ flex: `0 0 ${slotPct}`, width: slotPct, maxWidth: slotPct }}
            >
              {item.week}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MySubscriptions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [masterProfile, setMasterProfile] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersTotal, setSubscribersTotal] = useState(0);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscribersError, setSubscribersError] = useState("");

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (user && isCopyTradeDisabled(user)) {
      navigate("/pages/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const verifyMasterAccess = useCallback(async () => {
    let storedUser = null;
    try {
      const raw = sessionStorage.getItem("userData");
      if (raw) storedUser = JSON.parse(raw);
    } catch (_) {
      /* ignore */
    }

    try {
      const response = await getCopyTradeMasterStatus();
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code ?? 0);
      if (code && code !== AppConstants.responseOk && code !== 200) {
        navigate("/pages/copy-trade", { replace: true });
        return;
      }
      const payload = data?.DATA ?? data?.data ?? {};
      const status = String(payload?.STATUS ?? payload?.status ?? "none").toLowerCase();
      if (status !== "approved") {
        navigate("/pages/copy-trade", { replace: true });
        return;
      }

      const master = payload?.MASTER ?? payload?.master ?? payload;
      const profitShare = Number(payload?.PROFIT_SHARE ?? payload?.profitShare ?? 0);
      const growthRaw = payload?.PROFIT_SHARE_GROWTH_PERCENT ?? payload?.profitShareGrowthPercent;
      const growth = growthRaw === null || growthRaw === undefined || growthRaw === ""
        ? null
        : Number(growthRaw);
      const netCommission = Number(
        payload?.NET_TRADING_COMMISSION ?? payload?.netTradingCommission ?? profitShare
      );
      const netGrowthRaw =
        payload?.NET_TRADING_COMMISSION_GROWTH_PERCENT ??
        payload?.netTradingCommissionGrowthPercent;
      const netGrowth =
        netGrowthRaw === null || netGrowthRaw === undefined || netGrowthRaw === ""
          ? null
          : Number(netGrowthRaw);
      setMasterProfile({
        ...mapMasterProfile(master, storedUser),
        profitShare: Number.isFinite(profitShare) ? profitShare : 0,
        profitShareGrowth: Number.isFinite(growth) ? growth : null,
        netCommission: Number.isFinite(netCommission) ? netCommission : 0,
        netCommissionGrowth: Number.isFinite(netGrowth) ? netGrowth : null,
        commissionFlow: mapCommissionFlow(
          payload?.COMMISSION_FLOW ?? payload?.commissionFlow
        ),
      });
    } catch (_) {
      navigate("/pages/copy-trade", { replace: true });
    } finally {
      setStatusChecked(true);
    }
  }, [navigate]);

  useEffect(() => {
    verifyMasterAccess();
  }, [verifyMasterAccess]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch]);

  const fetchSubscribers = useCallback(async () => {
    if (!masterProfile) return;
    setSubscribersLoading(true);
    setSubscribersError("");
    try {
      const response = await getCopyTradeMasterSubscribers({
        SEARCH: appliedSearch.trim() || undefined,
        PER_PAGE: PER_PAGE,
        page: currentPage,
      });
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code ?? 0);
      if (code && code !== AppConstants.responseOk && code !== 200) {
        setSubscribers([]);
        setSubscribersTotal(0);
        setSubscribersError(
          t("msub_subscribers_load_error", {
            ns: "mobile",
            defaultValue: "Unable to load subscribers. Please try again.",
          })
        );
        return;
      }
      const { rows, total } = extractSubscribersPayload(response);
      setSubscribers(rows.map(mapSubscriberFromApi));
      setSubscribersTotal(total);
    } catch (_) {
      setSubscribers([]);
      setSubscribersTotal(0);
      setSubscribersError(
        t("msub_subscribers_load_error", {
          ns: "mobile",
          defaultValue: "Unable to load subscribers. Please try again.",
        })
      );
    } finally {
      setSubscribersLoading(false);
    }
  }, [masterProfile, appliedSearch, currentPage, t]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(subscribersTotal / PER_PAGE) || 1);
    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [subscribersTotal, currentPage]);

  const editInitialValues = useMemo(() => {
    if (!masterProfile) return null;
    return {
      name: masterProfile.name,
      login: masterProfile.login === "—" ? "" : masterProfile.login,
      commission: masterProfile.commission,
      commissionLogin: masterProfile.commissionLogin || masterProfile.login,
    };
  }, [masterProfile]);

  const totalItems = subscribersTotal;
  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / PER_PAGE) || 1)
  );
  const showingFrom = totalItems === 0 ? 0 : (safePage - 1) * PER_PAGE + 1;
  const showingTo = Math.min(safePage * PER_PAGE, totalItems);
  const pageLoading = !statusChecked || !masterProfile;

  const columns = useMemo(
    () => [
      {
        key: "clientAccount",
        label: t("msub_col_client_account", {
          ns: "mobile",
          defaultValue: "CLIENT ACCOUNT",
        }),
        className: "msub-col-account",
        render: (row) => <span className="ltr-content">{row.clientAccount}</span>,
      },
      {
        key: "subscriptionDate",
        label: t("msub_col_subscription_date", {
          ns: "mobile",
          defaultValue: "SUBSCRIPTION DATE",
        }),
        className: "msub-col-date",
        render: (row) => (
          <span className="msub-date-cell">
            <i className="fa-regular fa-calendar" aria-hidden />
            <span className="ltr-content">{formatDate(row.subscriptionDate)}</span>
          </span>
        ),
      },
      {
        key: "status",
        label: t("msub_col_status", { ns: "mobile", defaultValue: "STATUS" }),
        className: "msub-col-status",
        render: (row) => {
          const isActive = row.status === "active";
          return (
            <span className={`msub-status ${isActive ? "is-active" : "is-inactive"}`}>
              <span className="msub-status-dot" aria-hidden />
              {isActive
                ? t("msub_status_active", { ns: "mobile", defaultValue: "Active" })
                : t("msub_status_inactive", { ns: "mobile", defaultValue: "Inactive" })}
            </span>
          );
        },
      },
    ],
    [t]
  );

  const paginationSummary = (
    <span className="ib-clients-pagination-summary">
      {t("msub_showing_range", {
        ns: "mobile",
        defaultValue: "Showing {{from}} to {{to}} of {{total}} copiers",
        from: showingFrom,
        to: showingTo,
        total: totalItems,
      })}
    </span>
  );

  return (
    <div className="copy-trade-page msub-page">
      <Sidebar user={user} />
      <div className="copy-trade-content accounts-list-content">
        <section className="msub-assets-card" aria-busy={pageLoading}>
          <div className="msub-assets-main">
            <span className="msub-label">
              {t("msub_total_profit_share", {
                ns: "mobile",
                defaultValue: "TOTAL PROFIT SHARE",
              })}
            </span>
            <div className="msub-assets-value-row">
              {pageLoading ? (
                <>
                  <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--assets-value" />
                  <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--pill" />
                </>
              ) : (
                <>
                  <h1 className="msub-assets-value ltr-content">
                    {formatUsd(masterProfile?.profitShare || 0)}
                  </h1>
                  {masterProfile?.profitShareGrowth != null ? (
                    <span
                      className={`msub-trend-pill${
                        masterProfile.profitShareGrowth < 0 ? " is-down" : ""
                      }`}
                    >
                      <i
                        className={`fa-solid ${
                          masterProfile.profitShareGrowth < 0
                            ? "fa-arrow-down"
                            : "fa-arrow-up"
                        }`}
                        aria-hidden
                      />
                      {masterProfile.profitShareGrowth > 0 ? "+" : ""}
                      {masterProfile.profitShareGrowth.toFixed(1)}%
                    </span>
                  ) : null}
                </>
              )}
            </div>
          </div>
          <div className="msub-assets-actions">
            <button
              type="button"
              className="copy-trade-btn copy-trade-btn--primary"
              onClick={() => navigate("/pages/transfer")}
              disabled={pageLoading}
            >
              <i className="fa-solid fa-vault" aria-hidden />
              {t("msub_withdraw_profit", {
                ns: "mobile",
                defaultValue: "Withdraw Profit Share",
              })}
            </button>
          </div>
        </section>

        <section className="msub-volume-card" aria-busy={pageLoading}>
          <div className="msub-volume-header">
            <div>
              <h2 className="msub-section-title">
                {t("msub_volume_title", {
                  ns: "mobile",
                  defaultValue: "Subscription Volume Flow",
                })}
              </h2>
              <p className="msub-section-sub">
                {t("msub_volume_subtitle", {
                  ns: "mobile",
                  defaultValue: "Weekly trading commission vs. accumulated losses",
                })}
              </p>
            </div>
            <div className="msub-volume-legend">
              <span>
                <i className="msub-legend-swatch msub-legend-swatch--income" />
                {t("msub_legend_income", { ns: "mobile", defaultValue: "Income" })}
              </span>
              <span>
                <i className="msub-legend-swatch msub-legend-swatch--payback" />
                {t("msub_legend_paybacks", { ns: "mobile", defaultValue: "Pay-backs" })}
              </span>
            </div>
          </div>

          <div className="msub-volume-body">
            {pageLoading ? (
              <div className="msub-volume-chart msub-volume-chart--skeleton" aria-hidden>
                {Array.from({ length: 7 }).map((_, index) => (
                  <span
                    key={`msub-volume-bar-skeleton-${index}`}
                    className="accounts-skeleton-bar msub-skeleton msub-skeleton--bar"
                    style={{ height: `${40 + ((index * 17) % 45)}%` }}
                  />
                ))}
              </div>
            ) : (
              <VolumeFlowChart
                data={masterProfile?.commissionFlow || EMPTY_COMMISSION_FLOW}
                t={t}
              />
            )}
            <aside className="msub-volume-stats">
              <div className="msub-stat-block">
                <span className="msub-label">
                  {t("msub_gross_revenue", {
                    ns: "mobile",
                    defaultValue: "GROSS SUBSCRIPTION REVENUE",
                  })}
                </span>
                {pageLoading ? (
                  <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--stat-value" />
                ) : (
                  <strong className="ltr-content">
                    {formatUsd(masterProfile?.profitShare || 0)}
                  </strong>
                )}
              </div>
              <div className="msub-stat-block">
                <span className="msub-label">
                  {t("msub_net_commission", {
                    ns: "mobile",
                    defaultValue: "NET TRADING COMMISSION",
                  })}
                </span>
                {pageLoading ? (
                  <div className="msub-stat-value-row">
                    <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--stat-value" />
                    <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--pill" />
                  </div>
                ) : (
                  <div className="msub-stat-value-row">
                    <strong className="ltr-content">
                      {formatUsd(masterProfile?.netCommission || 0)}
                    </strong>
                    {masterProfile?.netCommissionGrowth != null ? (
                      <span
                        className={`msub-trend-pill${
                          masterProfile.netCommissionGrowth < 0 ? " is-down" : ""
                        }`}
                      >
                        <i
                          className={`fa-solid ${
                            masterProfile.netCommissionGrowth < 0
                              ? "fa-arrow-down"
                              : "fa-arrow-up"
                          }`}
                          aria-hidden
                        />
                        {masterProfile.netCommissionGrowth > 0 ? "+" : ""}
                        {masterProfile.netCommissionGrowth.toFixed(1)}%
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>

        <section className="msub-profile-card" aria-busy={pageLoading}>
          <div className="msub-profile-header">
            <h2 className="msub-section-title">
              {t("msub_profile_title", {
                ns: "mobile",
                defaultValue: "Master Trader Profile",
              })}
            </h2>
            {!pageLoading && masterProfile?.verified && (
              <span className="msub-verified-badge">
                <i className="fa-solid fa-circle-check" aria-hidden />
                {t("msub_verified", { ns: "mobile", defaultValue: "Verified" })}
              </span>
            )}
          </div>

          <div className="msub-profile-body">
            {pageLoading ? (
              <>
                <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--avatar" />
                <div className="msub-profile-info">
                  <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--name" />
                  <div className="msub-profile-pills">
                    <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--pill-wide" />
                    <span className="accounts-skeleton-bar msub-skeleton msub-skeleton--pill-wide" />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div
                  className="msub-profile-avatar"
                  style={{ backgroundColor: masterProfile.avatarColor }}
                >
                  {masterProfile.initials}
                </div>
                <div className="msub-profile-info">
                  <h3 className="msub-profile-name">{masterProfile.name}</h3>
                  <div className="msub-profile-pills">
                    <span className="msub-info-pill msub-info-pill--accent">
                      <i className="fa-solid fa-percent" aria-hidden />
                      {t("msub_commission_rate", {
                        ns: "mobile",
                        defaultValue: "Commission Rate: {{rate}}%",
                        rate: Number(masterProfile.commission).toFixed(2),
                      })}
                    </span>
                    <span className="msub-info-pill">
                      <i className="fa-regular fa-id-card" aria-hidden />
                      {t("msub_master_account", {
                        ns: "mobile",
                        defaultValue: "Master Account No. {{login}}",
                        login: masterProfile.login,
                      })}
                    </span>
                  </div>
                </div>
              </>
            )}
            <button
              type="button"
              className="copy-trade-btn copy-trade-btn--ghost msub-edit-btn"
              onClick={() => setIsEditOpen(true)}
              disabled={pageLoading}
            >
              {t("msub_edit_account", {
                ns: "mobile",
                defaultValue: "Edit Account",
              })}
            </button>
          </div>
        </section>

        <DataTable
          title={t("msub_cohort_title", {
            ns: "mobile",
            defaultValue: "Subscriber Cohort",
          })}
          columns={columns}
          data={subscribers}
          rowKey={(row) => row.id}
          currentPage={safePage}
          totalItems={totalItems}
          perPage={PER_PAGE}
          onPageChange={setCurrentPage}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onSearch={() => setAppliedSearch(searchTerm)}
          onClearSearch={() => {
            setSearchTerm("");
            setAppliedSearch("");
          }}
          searchPlaceholder={t("msub_search_placeholder", {
            ns: "mobile",
            defaultValue: "Search client...",
          })}
          loading={pageLoading || subscribersLoading}
          loadingMessage={t("loading", { ns: "mobile", defaultValue: "Loading..." })}
          error={pageLoading ? undefined : subscribersError || undefined}
          noDataMessage={t("msub_no_subscribers", {
            ns: "mobile",
            defaultValue: "No subscribers found.",
          })}
          containerClassName="container-fluid px-0 w-100 copy-trade-table-container msub-table-container"
          tableClassName="copy-trade-masters-table msub-subscribers-table"
          paginationLeading={paginationSummary}
        />
      </div>

      <BecomeMasterModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSuccess={(updated) => {
          if (updated) {
            setMasterProfile((prev) => ({
              ...mapMasterProfile(updated, user),
              verified: prev?.verified ?? true,
            }));
          } else {
            verifyMasterAccess();
          }
        }}
        t={t}
        mode="edit"
        initialValues={editInitialValues}
      />
    </div>
  );
}
